import React, { FC } from 'react';
import { getFeatureFlag } from 'src/components/FeatureFlags/getFeatureFlag';
import { showNotification } from 'src/components/NotificationBanner/showNotification';
import CommentLikeButton from './CommentLikeButton';
import DotSeparator from './DotSeparator';
import CommentThreadButton from './CommentThreadButton';
import { TCommentProps } from '../CommentsList';
import Spinner from '../../Spinner/Spinner';
import CommentReplyButton from './CommentReplyButton';
import { TCommentOrigin } from '../types';
import { useAnalytics } from '../../AnalyticsProvider/AnalyticsProvider';

export type TCommentActionsContainerProps = Pick<
  TCommentProps,
  'likesCount' | 'updateLike' | 'userAlreadyLikedComment'
> & {
  openModal: () => void;
  id: string;
  handleOpenCommentThread: () => void;
  childCommentsCount: number;
  loading: boolean;
  onReplyButtonClick: () => void;
  commentOrigin: TCommentOrigin;
};

const CommentActionsContainer: FC<TCommentActionsContainerProps> = ({
  id,
  updateLike,
  openModal,
  userAlreadyLikedComment,
  likesCount,
  handleOpenCommentThread,
  childCommentsCount,
  loading,
  onReplyButtonClick,
  commentOrigin,
}) => {
  const { sendEvent } = useAnalytics();
  const showCommentsThread = getFeatureFlag('commentsThread');

  const showErrorNotification = () =>
    showNotification({
      type: 'error',
      title: '',
      message: 'There was a problem with updating your like.',
    });

  const onLikeButtonClick = async () => {
    sendEvent('comment_like_button_click', {
      related_comment_id: id,
      related_comment_origin: commentOrigin,
      like_destination: commentOrigin,
      value: !userAlreadyLikedComment,
    });

    if (commentOrigin === 't2') {
      try {
        return updateLike?.();
      } catch (error) {
        return showErrorNotification();
      }
    }
  };

  const onLikeCounterClick = () => {
    sendEvent('comment_like_counter_click', {
      related_comment_id: id,
      related_comment_origin: commentOrigin,
    });
    openModal();
  };

  const onThreadButtonClick = () => {
    sendEvent('comment_thread_button_click', {
      related_comment_id: id,
      related_comment_origin: commentOrigin,
    });
    handleOpenCommentThread();
  };

  const handleReplyButtonClick = () => {
    sendEvent('comment_reply_button_click', {
      related_comment_id: id,
      related_comment_origin: commentOrigin,
    });
    onReplyButtonClick();
  };

  return (
    <div className="flex items-center gap-x-5">
      <CommentLikeButton
        onClick={onLikeButtonClick}
        onLikeCounterClick={onLikeCounterClick}
        likesCount={likesCount}
        userAlreadyLikedComment={userAlreadyLikedComment}
      />
      {showCommentsThread && childCommentsCount > 0 && (
        <>
          <DotSeparator />
          {loading ? (
            <div className="flex items-center justify-center w-10">
              <Spinner size={5} />
            </div>
          ) : (
            <CommentThreadButton onClick={onThreadButtonClick} childCommentsCount={childCommentsCount} />
          )}
        </>
      )}
      {showCommentsThread && <CommentReplyButton onClick={handleReplyButtonClick} />}
    </div>
  );
};
export default CommentActionsContainer;
